import React, { useState } from 'react'
import {
  MapPin, TrendingUp, ChevronDown, ChevronUp, Calculator,
  Lightbulb, BarChart2, ArrowRight, ArrowLeftRight, CheckCircle, XCircle,
  Briefcase, Star, Trophy, ChevronRight, Zap, Globe, Target
} from 'lucide-react'

// ── 직업 스토리 시나리오 데이터 ───────────────────────────────────────────────
const SCENARIOS = [
  {
    id: 'architect',
    emoji: '🏗️',
    title: '건축 설계사',
    category: '공학·건설',
    color: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    badge: 'bg-amber-100 text-amber-700',
    story: `건축 설계사 준영씨는 두 도로가 만나는 교차점을 계산해 건물 위치를 결정합니다.
도로 A: x + y = 10 (북쪽 도로)
도로 B: x − y = 2 (동쪽 도로)
"두 직선의 교점이 곧 건물이 들어설 최적 위치야!"`,
    situation: '두 도로의 방정식: x + y = 10, x − y = 2. 두 도로가 만나는 교점 (x, y)를 구하시오.',
    question: '연립방정식 { x + y = 10, x − y = 2 }의 해(교점) (x, y)는?',
    xLabel: '동서 방향 x (m)',
    yLabel: '남북 방향 y (m)',
    slope: -1,
    intercept: 10,
    slopeDesc: '도로 A의 기울기 −1 (우하향)',
    interceptDesc: 'y절편 10 (북쪽 한계)',
    equation: 'x + y = 10  /  x − y = 2',
    answer: '두 식을 더하면: 2x = 12 → x = 6\nx = 6을 대입: 6 + y = 10 → y = 4\n교점: (6, 4)',
    whyLinear: '두 직선(일차방정식)의 교점을 구하는 문제. 연립방정식의 해가 곧 그래프의 교점!',
    realAnswer: 6,
    unit: '(x좌표)',
    tableData: [
      { x: 4, y: 6 },
      { x: 5, y: 5 },
      { x: 6, y: 4 },
      { x: 7, y: 3 },
      { x: 8, y: 2 },
    ],
    quiz: [
      { q: '도로 A: x + y = 10 을 y = ax + b 꼴로 변환하면?', a: 'y = −x + 10 (기울기 −1, y절편 10)', hint: 'x를 이항: y = −x + 10' },
      { q: '두 도로의 교점 y좌표는?', a: 'y = 4 (x=6 대입)', hint: 'x=6을 x + y = 10에 대입: 6 + y = 10 → y = 4' },
    ],
    insight: '대형 건축사무소(삼성물산·현대건설 등)의 설계 엔지니어는 좌표계·연립방정식으로 건물·도로·교량의 교차점을 계산합니다.',
    jobs: ['건축 설계사', '토목 엔지니어', '도시 계획가', 'GIS 분석가'],
  },
  {
    id: 'economist',
    emoji: '📊',
    title: '경제 분석가',
    category: '경제·금융',
    color: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    badge: 'bg-emerald-100 text-emerald-700',
    story: `경제 분석가 지윤씨는 수요와 공급 곡선의 교점(균형점)을 찾아 시장 가격을 예측합니다.
수요: y = −2x + 100 (가격 y원, 수량 x개)
공급: y = 3x + 25
"두 직선의 교점이 바로 시장 균형 가격이야!"`,
    situation: '수요: y = −2x + 100, 공급: y = 3x + 25. 균형 수량(x)과 균형 가격(y)을 구하시오.',
    question: '수요와 공급이 일치하는 균형점 (x, y)는?',
    xLabel: '수량 x (개)',
    yLabel: '가격 y (원)',
    slope: -2,
    intercept: 100,
    slopeDesc: '수요 기울기 −2 (가격 오르면 수요 감소)',
    interceptDesc: '수요 y절편 100 (최대 가격)',
    equation: '수요: y = −2x + 100  /  공급: y = 3x + 25',
    answer: '−2x + 100 = 3x + 25\n75 = 5x → x = 15\ny = 3×15 + 25 = 70\n균형점: (15, 70)',
    whyLinear: '수요·공급 모두 일차함수로 나타낼 수 있고, 두 함수의 교점이 균형 가격·수량!',
    realAnswer: 70,
    unit: '원 (균형 가격)',
    tableData: [
      { x: 10, y: 55 },
      { x: 12, y: 61 },
      { x: 15, y: 70 },
      { x: 18, y: 79 },
      { x: 20, y: 85 },
    ],
    quiz: [
      { q: '수요 곡선 y = −2x + 100의 기울기는 무엇을 의미하나요?', a: '가격 1원 상승할 때 수요량이 2개 감소 (음의 기울기 = 수요 법칙)', hint: '기울기 a = −2: x 1 증가 → y 2 감소' },
      { q: '균형 수량 x = 15일 때 공급 함수로 균형 가격을 확인하면?', a: 'y = 3×15 + 25 = 70원 ✓', hint: 'x=15를 y = 3x + 25에 대입' },
    ],
    insight: '증권사·경제연구원·중앙은행 이코노미스트는 선형 모형(일차함수 연립)으로 단기 균형 가격을 추정합니다.',
    jobs: ['경제 분석가', '투자 전략가', '금융 리스크 매니저', '데이터 사이언티스트'],
  },
  {
    id: 'logistics',
    emoji: '🚚',
    title: '물류 최적화 전문가',
    category: '물류·SCM',
    color: 'from-blue-400 to-indigo-500',
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    badge: 'bg-blue-100 text-blue-700',
    story: `물류 전문가 민서씨는 두 배송 경로의 비용 방정식을 비교해 최적 경로를 찾습니다.
경로 A: y = 500x + 2000 (기본료 2000원 + km당 500원)
경로 B: y = 300x + 4000 (기본료 4000원 + km당 300원)
"두 직선이 교차하는 거리에서 비용이 같아지고, 그 이후 어느 경로가 더 저렴한지 결정해야 해!"`,
    situation: '경로 A 비용: y = 500x + 2000, 경로 B 비용: y = 300x + 4000. 두 경로의 비용이 같아지는 거리 x(km)는?',
    question: '500x + 2000 = 300x + 4000 을 풀면 x = ?',
    xLabel: '거리 x (km)',
    yLabel: '비용 y (원)',
    slope: 500,
    intercept: 2000,
    slopeDesc: '경로 A km당 +500원 (단위 비용)',
    interceptDesc: '경로 A 기본료 2000원 (y절편)',
    equation: '경로 A: y = 500x + 2000  /  경로 B: y = 300x + 4000',
    answer: '500x + 2000 = 300x + 4000\n200x = 2000\nx = 10 (km)\ny = 500×10 + 2000 = 7000원',
    whyLinear: '두 경로 모두 비용이 거리에 비례 → 일차함수. 교점에서 비용이 동일해지고 손익분기가 결정!',
    realAnswer: 10,
    unit: 'km (손익분기 거리)',
    tableData: [
      { x: 5, y: 4500 },
      { x: 8, y: 6000 },
      { x: 10, y: 7000 },
      { x: 12, y: 8000 },
      { x: 15, y: 9500 },
    ],
    quiz: [
      { q: 'x = 10 이전(예: x=5)에서 어떤 경로가 더 저렴한가요?', a: '경로 A: 500×5+2000=4500원, 경로 B: 300×5+4000=5500원 → 경로 A가 저렴', hint: 'x=5를 각 식에 대입해 비교하세요' },
      { q: 'x = 10 이후(예: x=15)에서 어떤 경로가 더 저렴한가요?', a: '경로 A: 500×15+2000=9500원, 경로 B: 300×15+4000=8500원 → 경로 B가 저렴', hint: 'x=15를 각 식에 대입해 비교하세요' },
    ],
    insight: '쿠팡·CJ대한통운·롯데글로벌로지스의 물류 최적화팀은 선형 비용 함수와 연립방정식으로 배송 경로를 선택합니다.',
    jobs: ['물류 최적화 전문가', 'SCM 분석가', '운영 리서처', '배송 경로 엔지니어'],
  },
]

// ── 직업 카드 데이터 ───────────────────────────────────────────────────────────
const CAREERS = [
  {
    icon: '🏗️',
    title: '건축·도시 설계사',
    example: '도로·건물의 교차점을 연립방정식으로 결정. CAD 소프트웨어도 내부적으로 일차방정식 연산.',
    formula: 'a₁x + b₁y = c₁  /  a₂x + b₂y = c₂  → 교점 (x,y)',
    salary: '연 5,000~9,000만원',
    color: 'from-amber-400 to-orange-500',
  },
  {
    icon: '📊',
    title: '경제·금융 분석가',
    example: '수요·공급 직선의 교점으로 균형 가격 예측. 선형 회귀로 경제지표 분석.',
    formula: '수요: y = −ax + b  /  공급: y = cx + d  → 균형점',
    salary: '연 6,000~1억원+',
    color: 'from-emerald-400 to-teal-500',
  },
  {
    icon: '🚚',
    title: '물류 최적화 전문가',
    example: '두 경로의 비용 함수 교점(손익분기)을 계산해 최적 배송 경로 선택.',
    formula: 'Cost_A(x) = Cost_B(x) → x* (손익분기 거리)',
    salary: '연 4,500~8,000만원',
    color: 'from-blue-400 to-indigo-500',
  },
  {
    icon: '💊',
    title: '의약품 연구원',
    example: '약물 농도와 시간의 관계를 선형 모델로 근사해 투약량·투여 간격 결정.',
    formula: 'C(t) = −kt + C₀  (농도 감소 모델)',
    salary: '연 5,500~1억원+',
    color: 'from-purple-400 to-violet-500',
  },
  {
    icon: '🔋',
    title: '에너지 엔지니어',
    example: '전력 수요·공급 곡선의 교점으로 전력 거래 가격(SMP) 산정.',
    formula: '수요: D(p) = a−bp  /  공급: S(p) = c+dp  → 균형',
    salary: '연 5,000~9,000만원',
    color: 'from-yellow-400 to-amber-500',
  },
  {
    icon: '🤖',
    title: 'AI·머신러닝 엔지니어',
    example: '선형 분류기(퍼셉트론)의 결정 경계가 바로 일차방정식. y = ax + b 로 클래스 구분.',
    formula: 'w₁x₁ + w₂x₂ + b = 0 (결정 경계)',
    salary: '연 6,000~1억 5천만원',
    color: 'from-slate-500 to-slate-700',
  },
]

// ── 도전 문제 데이터 ───────────────────────────────────────────────────────────
const CHALLENGES = [
  {
    id: 'ch1',
    emoji: '🎯',
    level: '⭐ 쉬움',
    levelColor: 'bg-green-100 text-green-700',
    question: '두 직선 y = x + 3 과 y = −x + 7 의 교점 (x, y)를 구하시오.',
    hint: '두 식을 같다고 놓으면: x + 3 = −x + 7 → 2x = 4 → x = ?',
    answer: '교점: (2, 5)',
    explanation: 'x + 3 = −x + 7 → 2x = 4 → x = 2. y = 2 + 3 = 5. 교점 (2, 5)!',
  },
  {
    id: 'ch2',
    emoji: '💹',
    level: '⭐⭐ 보통',
    levelColor: 'bg-yellow-100 text-yellow-700',
    question: '카페 A: 아메리카노 1잔 3,000원 + 회원가입비 5,000원. 카페 B: 1잔 4,000원 (무료 가입). 몇 잔부터 A가 더 저렴해지나요?',
    hint: 'A: y = 3000x + 5000, B: y = 4000x. 두 식이 같아질 때 x = ?',
    answer: '6잔 이상부터 A가 더 저렴',
    explanation: '3000x + 5000 = 4000x → 5000 = 1000x → x = 5. 5잔에서 같아지고, 6잔부터 A가 저렴!',
  },
  {
    id: 'ch3',
    emoji: '🏊',
    level: '⭐⭐ 보통',
    levelColor: 'bg-yellow-100 text-yellow-700',
    question: '수영장 A: 기본요금 2,000원 + 분당 100원. 수영장 B: 기본요금 없이 분당 150원. 어느 시점에서 A가 더 저렴해지나요?',
    hint: 'A: y = 100x + 2000, B: y = 150x. 두 식을 같다고 놓으면?',
    answer: '40분 이상 이용 시 A가 더 저렴',
    explanation: '100x + 2000 = 150x → 2000 = 50x → x = 40. 40분 이전은 B, 40분 이후는 A!',
  },
  {
    id: 'ch4',
    emoji: '🏙️',
    level: '⭐⭐⭐ 어려움',
    levelColor: 'bg-red-100 text-red-700',
    question: '두 직선 2x + y = 8, x − y = 1 의 교점을 구하고, 그 교점이 몇 사분면에 있는지 말하시오.',
    hint: '두 식을 더하면: 3x = 9 → x = 3. y = ?를 구한 후 사분면 판별!',
    answer: '교점 (3, 2) — 제1사분면',
    explanation: '2x + y = 8 과 x − y = 1 을 더하면 3x = 9 → x = 3. x = 3을 대입: 3 − y = 1 → y = 2. (3, 2): x > 0, y > 0 → 제1사분면!',
  },
]

const CATEGORY_COLORS: Record<string, string> = {
  '공학·건설': 'bg-amber-100 text-amber-700',
  '경제·금융': 'bg-emerald-100 text-emerald-700',
  '물류·SCM': 'bg-blue-100 text-blue-700',
}

// ── 계산기 컴포넌트 ────────────────────────────────────────────────────────────
type CalcMode = 'forward' | 'inverse'

interface CalcResult {
  mode: CalcMode
  inputVal: number
  outputVal: number
  steps: string[]
}

function ScenarioCalculator({ scenario }: { scenario: typeof SCENARIOS[0] }) {
  const [mode, setMode] = useState<CalcMode>('forward')
  const [inputVal, setInputVal] = useState('')
  const [result, setResult] = useState<CalcResult | null>(null)
  const [userAnswer, setUserAnswer] = useState('')
  const [checked, setChecked] = useState<boolean | null>(null)

  const reset = () => { setInputVal(''); setResult(null); setUserAnswer(''); setChecked(null) }
  const handleModeChange = (m: CalcMode) => { setMode(m); reset() }

  const calculate = () => {
    const v = parseFloat(inputVal)
    if (isNaN(v)) return
    if (mode === 'forward') {
      const raw = scenario.slope * v + scenario.intercept
      const y = Math.round(raw * 100) / 100
      setResult({
        mode: 'forward',
        inputVal: v,
        outputVal: y,
        steps: [
          `공식: ${scenario.equation.split('/')[0].trim()}`,
          `x = ${v} 대입`,
          `y = ${scenario.slope} × ${v} + ${scenario.intercept}`,
          `y = ${scenario.slope * v} + ${scenario.intercept}`,
          `y = ${y.toLocaleString()} ${scenario.unit}`,
        ],
      })
    } else {
      if (scenario.slope === 0) return
      const raw = (v - scenario.intercept) / scenario.slope
      const x = Math.round(raw * 100) / 100
      setResult({
        mode: 'inverse',
        inputVal: v,
        outputVal: x,
        steps: [
          `공식: ${scenario.equation.split('/')[0].trim()}`,
          `${scenario.yLabel.split(' ')[0]} = ${v} 대입`,
          `${v} = ${scenario.slope} × x + ${scenario.intercept}`,
          `${scenario.slope} × x = ${v} − ${scenario.intercept} = ${v - scenario.intercept}`,
          `x = ${v - scenario.intercept} ÷ ${scenario.slope} = ${x}`,
        ],
      })
    }
    setUserAnswer('')
    setChecked(null)
  }

  const checkAnswer = () => {
    if (!result) return
    const ua = parseFloat(userAnswer.replace(/,/g, ''))
    const tolerance = Math.abs(result.outputVal) * 0.01 + 0.5
    setChecked(Math.abs(ua - result.outputVal) <= tolerance)
  }

  const xLabelShort = scenario.xLabel.split(' ')[0]
  const yLabelShort = scenario.yLabel.split(' ')[0]

  return (
    <div className="card border-2 border-primary-100">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-slate-700 flex items-center gap-2">
          <Calculator size={15} className="text-primary-600" />
          직접 계산해보기
        </p>
        <div className="flex rounded-lg overflow-hidden border border-slate-200 text-xs">
          <button
            onClick={() => handleModeChange('forward')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'forward' ? 'bg-primary-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowRight size={11} />
            {xLabelShort} → {yLabelShort}
          </button>
          <button
            onClick={() => handleModeChange('inverse')}
            className={`px-3 py-1.5 font-medium transition-colors flex items-center gap-1 ${
              mode === 'inverse' ? 'bg-purple-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
            }`}
          >
            <ArrowLeftRight size={11} />
            {yLabelShort} → {xLabelShort}
          </button>
        </div>
      </div>

      <div className="mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
        <span className="text-xs text-slate-400 font-medium">함수식</span>
        <span className="font-mono font-bold text-primary-700 text-sm">{scenario.equation.split('/')[0].trim()}</span>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="flex-1">
          <label className="text-xs font-semibold text-slate-500">
            {mode === 'forward'
              ? `🔢 ${scenario.xLabel} 를 입력하세요`
              : `🔢 ${scenario.yLabel} 를 입력하세요`}
          </label>
          <input
            type="number"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setResult(null); setChecked(null) }}
            onKeyDown={e => e.key === 'Enter' && calculate()}
            placeholder="숫자를 입력하고 계산!"
            className="w-full mt-1 text-base border-2 border-slate-200 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={calculate}
            disabled={!inputVal}
            className={`px-5 py-2.5 rounded-xl font-bold text-sm transition-all ${
              mode === 'forward'
                ? 'bg-primary-600 hover:bg-primary-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
                : 'bg-purple-600 hover:bg-purple-700 text-white disabled:bg-slate-200 disabled:text-slate-400'
            }`}
          >
            계산!
          </button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 mb-3 flex items-center gap-1">
              📝 풀이 과정 (단계별)
            </p>
            <div className="space-y-1.5">
              {result.steps.map((step, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    i === result.steps.length - 1
                      ? 'bg-emerald-500 text-white'
                      : 'bg-amber-200 text-amber-700'
                  }`}>{i + 1}</span>
                  <span className={`text-sm font-mono ${
                    i === result.steps.length - 1
                      ? 'font-black text-emerald-700 text-base'
                      : 'text-amber-800'
                  }`}>{step}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl px-5 py-4 border-2 ${
            mode === 'forward'
              ? 'bg-primary-50 border-primary-300'
              : 'bg-purple-50 border-purple-300'
          }`}>
            <p className="text-xs font-semibold text-slate-500 mb-1">
              {mode === 'forward'
                ? `${scenario.xLabel} = ${result.inputVal} 일 때`
                : `${scenario.yLabel} = ${result.inputVal.toLocaleString()} ${scenario.unit} 일 때`}
            </p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-black ${mode === 'forward' ? 'text-primary-700' : 'text-purple-700'}`}>
                {result.outputVal.toLocaleString()}
              </span>
              <span className="text-base font-semibold text-slate-500">
                {mode === 'forward' ? scenario.unit : scenario.xLabel.split(' ')[1]?.replace(/[()]/g, '') || ''}
              </span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <p className="text-xs font-bold text-slate-600 mb-2">🧑‍🎓 답 맞춰보기 — 직접 계산한 후 입력해보세요!</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={userAnswer}
                onChange={e => { setUserAnswer(e.target.value); setChecked(null) }}
                onKeyDown={e => e.key === 'Enter' && checkAnswer()}
                placeholder={`내 답: __ ${mode === 'forward' ? scenario.unit : ''}`}
                className={`flex-1 text-sm border-2 rounded-lg px-3 py-2 focus:outline-none transition-all ${
                  checked === null ? 'border-slate-200' : checked ? 'border-emerald-400 bg-emerald-50' : 'border-red-400 bg-red-50'
                }`}
              />
              <button
                onClick={checkAnswer}
                disabled={!userAnswer}
                className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm font-medium disabled:bg-slate-200 disabled:text-slate-400 hover:bg-slate-800 transition-colors"
              >
                확인
              </button>
            </div>
            {checked !== null && (
              <div className={`mt-2 flex items-center gap-2 text-sm font-semibold ${checked ? 'text-emerald-600' : 'text-red-600'}`}>
                {checked
                  ? <><CheckCircle size={16} /> 정답이에요! 🎉 완벽합니다!</>
                  : <><XCircle size={16} /> 아쉬워요. 풀이 과정을 다시 확인해보세요!</>}
              </div>
            )}
          </div>

          <button onClick={reset} className="text-xs text-slate-400 hover:text-slate-600 transition-colors">
            ↺ 다시 입력하기
          </button>
        </div>
      )}
    </div>
  )
}

// ── 퀴즈 컴포넌트 ─────────────────────────────────────────────────────────────
function QuizSection({ quiz }: { quiz: typeof SCENARIOS[0]['quiz'] }) {
  const [revealed, setRevealed] = useState<Record<number, boolean>>({})
  const [showHint, setShowHint] = useState<Record<number, boolean>>({})

  return (
    <div className="space-y-3">
      {quiz.map((q, i) => (
        <div key={i} className="border border-slate-200 rounded-xl p-4 bg-white">
          <p className="text-sm font-semibold text-slate-800 mb-2">❓ {q.q}</p>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowHint(h => ({ ...h, [i]: !h[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
            >
              <Lightbulb size={11} /> 힌트
            </button>
            <button
              onClick={() => setRevealed(r => ({ ...r, [i]: !r[i] }))}
              className="text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors"
            >
              {revealed[i] ? '정답 숨기기' : '정답 보기'}
            </button>
          </div>
          {showHint[i] && (
            <div className="mt-2 bg-amber-50 rounded-lg px-3 py-2 text-xs text-amber-700">
              💡 힌트: <span className="font-mono">{q.hint}</span>
            </div>
          )}
          {revealed[i] && (
            <div className="mt-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <span className="text-sm font-bold text-emerald-700">✅ 정답: {q.a}</span>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 도전 문제 카드 컴포넌트 ───────────────────────────────────────────────────
function ChallengeCard({ ch }: { ch: typeof CHALLENGES[0] }) {
  const [showHint, setShowHint] = useState(false)
  const [revealed, setRevealed] = useState(false)

  return (
    <div className="card border border-slate-200">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{ch.emoji}</span>
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${ch.levelColor}`}>{ch.level}</span>
        </div>
      </div>
      <p className="text-sm text-slate-800 font-medium leading-relaxed mb-3">{ch.question}</p>
      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setShowHint(h => !h)}
          className="text-xs px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition-colors flex items-center gap-1"
        >
          <Lightbulb size={11} /> 힌트
        </button>
        <button
          onClick={() => setRevealed(r => !r)}
          className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors flex items-center gap-1"
        >
          <Trophy size={11} /> {revealed ? '정답 숨기기' : '정답 확인'}
        </button>
      </div>
      {showHint && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
          💡 {ch.hint}
        </div>
      )}
      {revealed && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 space-y-2">
          <p className="text-sm font-bold text-emerald-700">✅ 정답: {ch.answer}</p>
          <p className="text-xs text-emerald-600 leading-relaxed">{ch.explanation}</p>
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ───────────────────────────────────────────────────────────────
type Tab = 'scenarios' | 'careers' | 'challenges'

export default function F2_2RealWorldPage() {
  const [selected, setSelected] = useState(SCENARIOS[0])
  const [showTable, setShowTable] = useState(false)
  const [filterCat, setFilterCat] = useState<string>('전체')
  const [activeTab, setActiveTab] = useState<Tab>('scenarios')

  const categories = ['전체', ...Array.from(new Set(SCENARIOS.map(s => s.category)))]
  const filtered = filterCat === '전체' ? SCENARIOS : SCENARIOS.filter(s => s.category === filterCat)

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'scenarios', label: '시나리오 탐색', icon: <MapPin size={14} /> },
    { id: 'careers', label: '직업과 연립방정식', icon: <Briefcase size={14} /> },
    { id: 'challenges', label: '도전 문제', icon: <Trophy size={14} /> },
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center">
            <Globe size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">꿈꾸는 직업과 일차방정식</h1>
            <p className="text-sm text-slate-500">건축가, 경제학자, 물류 전문가 — 모두 연립방정식으로 교점을 구합니다!</p>
          </div>
        </div>

        {/* Career Story Banner */}
        <div className="mt-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-5">
          <p className="font-bold text-lg mb-1">🔗 두 직선의 교점 = 현실의 최적 결정점!</p>
          <p className="text-sm opacity-90 mb-3">건물 위치, 균형 가격, 최적 경로 — 모두 연립방정식(두 직선의 교점)으로 결정됩니다.</p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {[
              { icon: '🏗️', job: '건축가' },
              { icon: '📊', job: '경제학자' },
              { icon: '🚚', job: '물류전문가' },
              { icon: '💊', job: '연구원' },
              { icon: '🔋', job: '에너지엔지니어' },
              { icon: '🤖', job: 'AI개발자' },
            ].map(({ icon, job }) => (
              <div key={job} className="bg-white/20 rounded-xl px-2 py-2 text-center">
                <p className="text-2xl">{icon}</p>
                <p className="text-xs font-bold mt-1">{job}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id
                ? 'bg-white text-primary-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-800'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── TAB 1: 시나리오 ── */}
      {activeTab === 'scenarios' && (
        <>
          {/* Category Filter */}
          <div className="flex gap-2 mb-4 flex-wrap">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setFilterCat(cat)}
                className={`text-xs px-4 py-2 rounded-full font-medium transition-colors ${
                  filterCat === cat
                    ? 'bg-primary-600 text-white'
                    : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Scenario Cards (Left List) */}
            <div className="space-y-3">
              {filtered.map(scenario => (
                <button
                  key={scenario.id}
                  onClick={() => { setSelected(scenario); setShowTable(false) }}
                  className={`w-full text-left rounded-2xl border-2 p-4 transition-all ${
                    selected.id === scenario.id
                      ? `${scenario.border} ${scenario.bg} shadow-md`
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{scenario.emoji}</span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-slate-800">{scenario.title}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[scenario.category] ?? 'bg-slate-100 text-slate-600'}`}>
                          {scenario.category}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{scenario.situation}</p>
                      <div className="mt-1.5 font-mono text-xs text-primary-600 font-semibold">{scenario.equation.split('/')[0].trim()}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Detail Panel (Right) */}
            <div className="lg:col-span-2 space-y-4">
              {/* Situation Card */}
              <div className={`rounded-2xl border-2 ${selected.border} ${selected.bg} p-5`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-4xl">{selected.emoji}</span>
                  <div>
                    <h2 className="text-xl font-bold text-slate-800">{selected.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${CATEGORY_COLORS[selected.category] ?? 'bg-slate-100 text-slate-600'}`}>
                      {selected.category}
                    </span>
                  </div>
                </div>

                {/* Story */}
                <div className="mb-4 bg-white/70 rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-bold text-slate-500 mb-2">📖 이야기로 만나는 일차방정식</p>
                  <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">
                    {selected.story}
                  </p>
                </div>

                <p className="text-sm font-semibold text-slate-600 mb-1">📌 수학적 상황</p>
                <p className="text-sm text-slate-700 leading-relaxed mb-3">{selected.situation}</p>

                {/* Equation Breakdown */}
                <div className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-xs font-semibold text-slate-500 mb-3">📐 방정식으로 나타내기</p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-lg font-black text-primary-700 font-mono">{selected.equation}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-2.5">
                      <p className="text-xs text-orange-500 font-semibold">기울기 (a = {selected.slope})</p>
                      <p className="text-xs text-orange-700 mt-0.5">{selected.slopeDesc}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                      <p className="text-xs text-blue-500 font-semibold">y절편 (b = {selected.intercept})</p>
                      <p className="text-xs text-blue-700 mt-0.5">{selected.interceptDesc}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Question & Answer */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-2">
                  <Target size={14} className="inline mr-1 text-yellow-500" />
                  핵심 문제
                </p>
                <p className="text-base text-slate-800 font-medium">{selected.question}</p>
                <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
                  <p className="text-xs font-bold text-emerald-600 mb-2">✅ 풀이 과정</p>
                  {selected.answer.split('\n').map((line, i) => (
                    <div key={i} className="flex gap-2 mb-1">
                      <span className="w-5 h-5 bg-emerald-500 text-white rounded-full text-xs flex items-center justify-center flex-shrink-0 font-bold mt-0.5">
                        {i + 1}
                      </span>
                      <p className="text-sm text-emerald-700 font-mono">{line}</p>
                    </div>
                  ))}
                </div>
                <div className="mt-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  <p className="text-xs text-amber-700">
                    <span className="font-semibold">왜 일차방정식인가?</span> {selected.whyLinear}
                  </p>
                </div>
              </div>

              {/* Table / Graph Toggle */}
              <div className="card">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    <BarChart2 size={15} className="text-primary-500" />
                    값 변화표로 보기
                  </p>
                  <button
                    onClick={() => setShowTable(!showTable)}
                    className="text-xs flex items-center gap-1 text-slate-500 hover:text-slate-700 bg-slate-100 px-3 py-1 rounded-full"
                  >
                    {showTable ? '접기' : '표 펼치기'}
                    {showTable ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>

                {showTable && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr>
                          <th className="border border-slate-200 px-3 py-2 bg-orange-50 text-orange-700 text-left">
                            {selected.xLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-primary-50 text-primary-700 text-left">
                            {selected.yLabel}
                          </th>
                          <th className="border border-slate-200 px-3 py-2 bg-slate-50 text-slate-600 text-left">
                            변화율 (Δy/Δx)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.tableData.map((row, i) => (
                          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-orange-600">{row.x}</td>
                            <td className="border border-slate-200 px-3 py-2 font-mono font-bold text-primary-700">
                              {row.y.toLocaleString()}
                            </td>
                            <td className="border border-slate-200 px-3 py-2 text-xs text-slate-500">
                              {i === 0 ? '—' : (
                                <span className="font-mono text-emerald-600">
                                  {((row.y - selected.tableData[i - 1].y) / (row.x - selected.tableData[i - 1].x)).toFixed(1)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <p className="text-xs text-slate-400 mt-2 text-center">
                      두 직선의 교점에서 값이 일치 → 연립방정식의 해!
                    </p>
                  </div>
                )}
              </div>

              {/* Calculator */}
              <ScenarioCalculator scenario={selected} />

              {/* Quiz */}
              <div className="card">
                <p className="text-sm font-semibold text-slate-700 mb-3">🧩 연습 문제</p>
                <QuizSection quiz={selected.quiz} />
              </div>

              {/* Jobs */}
              <div className="card bg-slate-50 border border-slate-200">
                <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Briefcase size={14} className="text-primary-500" />
                  이 상황에서 연립방정식을 쓰는 직업
                </p>
                <div className="flex flex-wrap gap-2">
                  {selected.jobs.map(job => (
                    <span key={job} className="text-xs bg-primary-100 text-primary-700 px-3 py-1.5 rounded-full font-medium flex items-center gap-1">
                      <Star size={10} /> {job}
                    </span>
                  ))}
                </div>
              </div>

              {/* Insight */}
              <div className="rounded-2xl bg-gradient-to-r from-slate-800 to-slate-700 text-white p-5">
                <p className="text-sm font-bold mb-1 flex items-center gap-2">
                  <TrendingUp size={15} /> 실제로 어디서 쓰이나요?
                </p>
                <p className="text-sm text-slate-300 leading-relaxed">{selected.insight}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {['연립방정식', '두 직선의 교점', '최적화 결정', '균형점 분석'].map(tag => (
                    <span key={tag} className="text-xs bg-white/10 px-2 py-1 rounded-full">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Summary */}
          <div className="mt-8 card bg-gradient-to-br from-primary-50 to-indigo-50 border-primary-200">
            <h3 className="font-bold text-slate-800 mb-4 text-base">📌 일차방정식 실생활 공통 패턴</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { icon: '🔗', title: '교점 = 결정점', desc: '두 직선이 만나는 점 → 건물 위치, 균형 가격, 손익분기점 등 현실의 최적 결정!', color: 'text-indigo-600' },
                { icon: '⚖️', title: '연립 = 두 조건 동시', desc: '두 방정식을 동시에 만족하는 해 → 두 가지 조건이 모두 성립하는 현실의 답!', color: 'text-emerald-600' },
                { icon: '📐', title: '그래프로 확인', desc: '방정식 → 직선 → 교점을 그래프로 시각화하면 이해가 훨씬 쉬워진다!', color: 'text-orange-600' },
              ].map(({ icon, title, desc, color }) => (
                <div key={title} className="bg-white rounded-xl p-4 border border-slate-100">
                  <p className="text-2xl mb-2">{icon}</p>
                  <p className={`font-bold text-sm ${color}`}>{title}</p>
                  <p className="text-xs text-slate-600 mt-1 leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── TAB 2: 직업 탐색 ── */}
      {activeTab === 'careers' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">💼 연립방정식을 사용하는 직업들</h2>
            <p className="text-sm text-indigo-100">
              연립방정식(두 직선의 교점)은 전 세계 수천만 직업인이 매일 활용하는 핵심 도구입니다.
              지금 배우는 것이 미래 직업의 기반이 됩니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-8">
            {CAREERS.map(career => (
              <div key={career.title} className="card border border-slate-200 hover:shadow-md transition-shadow">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${career.color} flex items-center justify-center text-2xl mb-3`}>
                  {career.icon}
                </div>
                <h3 className="font-bold text-slate-800 mb-1">{career.title}</h3>
                <p className="text-xs text-slate-500 mb-3 leading-relaxed">{career.example}</p>
                <div className="bg-slate-50 rounded-lg p-3 mb-3">
                  <p className="text-xs text-slate-400 mb-1">실제 사용 공식</p>
                  <code className="text-xs font-mono text-primary-700 font-bold">{career.formula}</code>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-medium">
                    💰 {career.salary}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* AI & 미래 직업 */}
          <div className="card border border-slate-200 bg-gradient-to-br from-slate-800 to-slate-900 text-white mb-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <Zap size={20} className="text-yellow-400" /> AI 시대에도 연립방정식이 필요한 이유
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                {
                  title: '선형 분류기의 결정 경계',
                  desc: '머신러닝의 퍼셉트론·SVM은 w₁x + w₂y = b (일차방정식)로 데이터를 분류합니다.',
                  color: 'border-yellow-400',
                },
                {
                  title: '최적화 문제의 연립 조건',
                  desc: '공학·경영·물류의 최적화 문제는 여러 일차방정식을 연립해 최적점을 구합니다.',
                  color: 'border-blue-400',
                },
                {
                  title: '균형 분석 = 연립방정식',
                  desc: '게임 이론·경제 균형·네트워크 흐름 모두 다수의 방정식 교점을 구하는 문제.',
                  color: 'border-emerald-400',
                },
                {
                  title: '컴퓨터 그래픽스',
                  desc: '두 선분의 교점 계산, 충돌 감지, 광선 추적 — 모두 연립방정식 풀기!',
                  color: 'border-purple-400',
                },
              ].map(item => (
                <div key={item.title} className={`border-l-4 ${item.color} pl-4 py-2`}>
                  <p className="font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-xs text-slate-300 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 학습 경로 */}
          <div className="card border border-primary-200 bg-primary-50">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <ChevronRight size={18} className="text-primary-600" /> 연립방정식에서 시작하는 학습 경로
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { step: '중2', topic: '일차방정식', active: true },
                { step: '중2', topic: '연립방정식', active: true },
                { step: '중3', topic: '이차방정식', active: false },
                { step: '고1', topic: '행렬·벡터', active: false },
                { step: '고2', topic: '선형대수', active: false },
                { step: '대학', topic: '최적화 이론', active: false },
                { step: '직업', topic: 'AI·데이터사이언스', active: false },
              ].map((item, i) => (
                <React.Fragment key={`${item.step}-${item.topic}`}>
                  <div className={`rounded-xl px-3 py-2 text-center ${item.active ? 'bg-primary-600 text-white' : 'bg-white border border-slate-200 text-slate-600'}`}>
                    <p className="text-xs font-bold">{item.step}</p>
                    <p className="text-xs mt-0.5">{item.topic}</p>
                  </div>
                  {i < 6 && <ChevronRight size={14} className="text-slate-400 flex-shrink-0" />}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: 도전 문제 ── */}
      {activeTab === 'challenges' && (
        <div>
          <div className="mb-6 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-500 text-white p-6">
            <h2 className="text-xl font-bold mb-2">🏆 실생활 도전 문제</h2>
            <p className="text-sm text-amber-100">
              실제 상황에서 연립방정식을 직접 만들고 풀어보세요.
              두 직선의 교점을 구하는 것이 핵심입니다!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
            {CHALLENGES.map(ch => (
              <ChallengeCard key={ch.id} ch={ch} />
            ))}
          </div>

          {/* 핵심 공식 정리 */}
          <div className="card border border-slate-200">
            <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
              <Star size={16} className="text-amber-500" /> 실생활 문제 풀기 비법
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                {
                  step: '① 방정식 세우기',
                  desc: '"두 가지 조건이 있는가?" → 각각 일차방정식으로 표현',
                  example: '경로 A: y = 500x + 2000, 경로 B: y = 300x + 4000',
                  color: 'bg-blue-50 border-blue-200 text-blue-800',
                },
                {
                  step: '② 교점 구하기',
                  desc: '"두 식이 같아지는 점" → 연립해서 x, y를 계산',
                  example: '500x + 2000 = 300x + 4000 → x = 10',
                  color: 'bg-orange-50 border-orange-200 text-orange-800',
                },
                {
                  step: '③ 결론 내기',
                  desc: '"교점 전후로 어떻게 달라지나?" → 실생활 해석',
                  example: 'x < 10: 경로 A 저렴, x > 10: 경로 B 저렴',
                  color: 'bg-emerald-50 border-emerald-200 text-emerald-800',
                },
              ].map(item => (
                <div key={item.step} className={`border rounded-xl p-4 ${item.color}`}>
                  <p className="font-bold text-sm mb-2">{item.step}</p>
                  <p className="text-xs leading-relaxed mb-2">{item.desc}</p>
                  <div className="bg-white/60 rounded-lg px-2 py-1">
                    <p className="text-xs font-mono font-bold">{item.example}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-primary-50 border border-primary-200 rounded-xl p-4 text-center">
              <p className="text-sm font-bold text-primary-700">{'연립방정식: { a₁x + b₁y = c₁,  a₂x + b₂y = c₂ }'}</p>
              <p className="text-xs text-primary-500 mt-1">두 직선의 교점 = 두 조건을 동시에 만족하는 (x, y) → 현실의 최적 결정!</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
